import * as React from 'react';
import cx from 'classnames';
import { useStaticQuery, graphql } from 'gatsby';
import {
  useForm,
  type FieldError,
  type FieldErrors,
  type SubmitHandler,
} from 'react-hook-form';

import { Input } from '../Input/Input';
import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Link } from '../Link/Link';
import { Honeypot } from '../Honeypot/Honeypot';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { routes } from '../../config/routes';
import { useSiteMetadata } from '../../utils/useSiteMetadata';
import { track } from '../../utils/analytics';
import { useSetTemporaryState } from '../../utils/useSetTemporaryState';
import { fetchForm } from '../../utils/http';
import { EmailNewsletterFormLoader } from './components/EmailNewsletterFormLoader/EmailNewsletterFormLoader';
import { SubmitError } from '../SubmitError/SubmitError';

import * as cs from './EmailNewsletterForm.module.css';

interface Props {
  className?: string;
  type: 'list' | 'post' | 'footer';
  light?: boolean;
}

interface FormFields {
  email: string;
}

type FormData = {
  newsletter_subscription: FormFields;
  name: string;
};

const notEmpty = (message: string) => (v?: string | FormFields) =>
  (v && typeof v === 'string' && v.trim().length > 0) || message;

export const EmailNewsletterForm = ({
  className,
  type,
  light,
  ...rest
}: Props) => {
  const data = useStaticQuery(graphql`
    query SubscribeQuery {
      snippet(slug: { eq: "subscribe" }) {
        id
        data
      }
    }
  `);

  const snippet: CustomTypes.SubscribeSnippet = data.snippet.data;

  const [rendered, setRendered] = React.useState(false);

  const [loading, setLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState(false);

  const [success, setSuccess] = useSetTemporaryState(false, 5400);
  const [shouldShake, setShouldShake] = useSetTemporaryState(false, 350);
  const { subscribeFormUrl } = useSiteMetadata();

  const [successMorphingOut, setSuccessMorphingOut] = useSetTemporaryState(
    false,
    600,
  );

  const interactionInfoSent = React.useRef(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
    setFocus,
  } = useForm<FormData>();

  const interactedFieldsCount = Object.keys(dirtyFields).length;

  React.useEffect(() => {
    setRendered(true);
  }, []);

  React.useEffect(() => {
    if (!interactionInfoSent.current && interactedFieldsCount > 0) {
      track('newsletter-subscription-fill');
      interactionInfoSent.current = true;
    }
  }, [interactedFieldsCount]);

  const onSubmit: SubmitHandler<FormData> = (data) => {
    if (data.name) {
      // It's a bot!
      track('newsletter-subscription-spam');
      return;
    }

    if (loading) return;

    const reqBody = {
      email: (data.newsletter_subscription.email || '').trim(),
    };

    track('newsletter-subscription-submit');

    setSubmitError(false);
    setLoading(true);

    fetchForm(subscribeFormUrl, {
      method: 'post',
      body: new URLSearchParams({
        'newsletter_subscription[email]': reqBody.email,
      }),
    })
      .then((response) => {
        if (response.ok) {
          setSuccess(true);
          setTimeout(() => {
            setLoading(false);
            setSuccessMorphingOut(true);
            setTimeout(() => {
              reset();
            }, 600);
          }, 5000);
        } else {
          throw new Error(
            `Email subscription form submit error: ${response.text()}`,
          );
        }
      })
      .catch(() => {
        setLoading(false);
        setSubmitError(true);
        track('newsletter-subscription-server-error');
      });
  };

  function onError(errors: FieldErrors<FormData>) {
    (Object.keys(errors) as Array<keyof typeof errors>).forEach((errorKey) => {
      track(
        'newsletter-subscription-validation-error',
        `${errorKey}-${errors[errorKey]?.type}`,
      );
    });
    if (shouldShake) return;
    setShouldShake(true);

    if (
      errors &&
      errors.newsletter_subscription &&
      errors.newsletter_subscription.email &&
      ['required', 'notEmpty'].includes(
        errors.newsletter_subscription.email.type,
      )
    ) {
      setFocus('newsletter_subscription[email]' as keyof FormData);
      reset();
    }
  }

  const getError = () => {
    if (
      errors &&
      errors.newsletter_subscription &&
      errors.newsletter_subscription.email
    ) {
      return (
        (errors.newsletter_subscription.email as FieldError).message ||
        snippet.errors.generic
      );
    }
  };

  const formDisabled = loading || successMorphingOut || success;

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onError)}
      aria-busy={loading}
      action={subscribeFormUrl}
      method="post"
      className={cx(cs.form, cs[type], { [cs.light]: light }, className)}
    >
      <article className={cs.wrapper} {...rest} id="newsletter">
        {Boolean(Object.keys(errors).length) && (
          <VisuallyHidden role="alert">{snippet.errorAria}</VisuallyHidden>
        )}
        <h3 className={cs.title}>{snippet.title}</h3>
        <p className={cs.description}>{snippet.description}</p>
        <div className={cs.formFields}>
          <Honeypot name="name" type={type} />
          <Input
            id={`newsletter_subscription[email]_${type}`}
            as="input"
            autocomplete="email"
            label={snippet.placeholder}
            placeholder={snippet.placeholder}
            type={rendered ? '' : 'email'}
            disabled={formDisabled}
            error={getError()}
            wrapperClassName={cs.input}
            hiddenLabel
            light={light}
            {...register('newsletter_subscription[email]' as keyof FormData, {
              required: { value: true, message: snippet.errors.required },
              maxLength: {
                value: 500,
                message: snippet.errors.max,
              },
              validate: {
                notEmpty: notEmpty(snippet.errors.required),
              },
              pattern: {
                value: /^[A-Za-z0-9_!#$%&'*+\\/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/gim,
                message: snippet.errors.invalid,
              },
            })}
          />
          <SubmitError
            className={cs.error}
            show={submitError}
            onCancel={() => {
              setSubmitError(false);
            }}
          />
          <div className={cx(cs.submitWrapper, { [cs.shake]: shouldShake })}>
            <Button
              size="lg"
              variant="solid"
              theme="current_text"
              className={cx(cs.button, {
                [cs.morphingIn]: successMorphingOut,
                [cs.morphingOut]: loading,
              })}
              type="submit"
              disabled={formDisabled}
            >
              <span className={cs.submitText}>{snippet.button}</span>
            </Button>
            <EmailNewsletterFormLoader
              snippet={snippet}
              loading={loading}
              success={success}
              morphingOut={successMorphingOut}
            />
          </div>
        </div>
        <div className={cs.rss}>
          <Icon name="rss" size={20} />
          <div>
            {snippet.or}{' '}
            <Link target="_blank" to={routes.blogFeed}>
              {snippet.rss}
            </Link>
          </div>
        </div>
      </article>
    </form>
  );
};
